import React, { useState } from 'react';
import {
  Destination,
  Passenger,
  Weather,
  Temperature,
  Time,
  Coupon,
  Expiration,
  Gender,
  Age,
  MaritalStatus,
  Education,
  Occupation,
  Income,
  Car,
  FrequencyCategory,
  CouponAssessmentInput,
  CouponAssessmentResponse,
} from '../types/coupons';
import { api } from '../services/api';
import { getEnumLabel } from '../utils/formatters';
import './CouponAssessmentForm.css';

const CouponAssessmentForm: React.FC = () => {
  const [formData, setFormData] = useState<CouponAssessmentInput>({
    destination: Destination.NO_URGENT_PLACE,
    passenger: Passenger.ALONE,
    weather: Weather.SUNNY,
    temperature: Temperature.MILD,
    time: Time.TWO_PM,
    coupon: Coupon.RESTAURANT_LESS_THAN_20,
    expiration: Expiration.ONE_DAY,
    gender: Gender.MALE,
    age: Age.AGE_26,
    maritalStatus: MaritalStatus.SINGLE,
    has_children: false,
    education: Education.BACHELORS_DEGREE,
    occupation: Occupation.COMPUTER_MATHEMATICAL,
    income: Income.INCOME_50000_62499,
    car: Car.DO_NOT_DRIVE,
    Bar: FrequencyCategory.NEVER,
    CoffeeHouse: FrequencyCategory.NEVER,
    CarryAway: FrequencyCategory.NEVER,
    RestaurantLessThan20: FrequencyCategory.NEVER,
    Restaurant20To50: FrequencyCategory.NEVER,
    toCoupon_GEQ5min: true,
    toCoupon_GEQ15min: false,
    toCoupon_GEQ25min: false,
    direction_same: true,
    direction_opp: false,
  });

  const [result, setResult] = useState<CouponAssessmentResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await api.predictCouponAcceptance(formData);

      if (response.status === 'success' && response.data) {
        setResult(response.data);
      } else {
        setError(response.error || 'An error occurred');
      }
    } catch (err) {
      setError('Failed to submit form');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const renderEnumOptions = (enumObj: Record<string, string>) => {
    return Object.entries(enumObj).map(([key, value]) => (
      <option key={value} value={value}>
        {getEnumLabel(key)}
      </option>
    ));
  };

  return (
    <div className="coupon-assessment-form">
      <h2>Coupon Acceptance Prediction</h2>
      <p>Fill out the form below to predict whether a coupon will be accepted.</p>

      <form onSubmit={handleSubmit}>
        <div className="form-section">
          <h3>Trip Information</h3>

          <div className="form-group">
            <label htmlFor="destination">Destination:</label>
            <select
              id="destination"
              name="destination"
              value={formData.destination}
              onChange={handleChange}
            >
              {renderEnumOptions(Destination)}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="passenger">Passenger:</label>
            <select
              id="passenger"
              name="passenger"
              value={formData.passenger}
              onChange={handleChange}
            >
              {renderEnumOptions(Passenger)}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="weather">Weather:</label>
            <select id="weather" name="weather" value={formData.weather} onChange={handleChange}>
              {renderEnumOptions(Weather)}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="temperature">Temperature:</label>
            <select
              id="temperature"
              name="temperature"
              value={formData.temperature}
              onChange={handleChange}
            >
              {renderEnumOptions(Temperature)}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="time">Time:</label>
            <select id="time" name="time" value={formData.time} onChange={handleChange}>
              {renderEnumOptions(Time)}
            </select>
          </div>
        </div>

        <div className="form-section">
          <h3>Coupon Information</h3>

          <div className="form-group">
            <label htmlFor="coupon">Coupon Type:</label>
            <select id="coupon" name="coupon" value={formData.coupon} onChange={handleChange}>
              {renderEnumOptions(Coupon)}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="expiration">Expiration:</label>
            <select
              id="expiration"
              name="expiration"
              value={formData.expiration}
              onChange={handleChange}
            >
              {renderEnumOptions(Expiration)}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="toCoupon_GEQ5min">Time to coupon ≥ 5 min:</label>
            <input
              type="checkbox"
              id="toCoupon_GEQ5min"
              name="toCoupon_GEQ5min"
              checked={formData.toCoupon_GEQ5min}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="toCoupon_GEQ15min">Time to coupon ≥ 15 min:</label>
            <input
              type="checkbox"
              id="toCoupon_GEQ15min"
              name="toCoupon_GEQ15min"
              checked={formData.toCoupon_GEQ15min}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="toCoupon_GEQ25min">Time to coupon ≥ 25 min:</label>
            <input
              type="checkbox"
              id="toCoupon_GEQ25min"
              name="toCoupon_GEQ25min"
              checked={formData.toCoupon_GEQ25min}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="direction_same">Same direction as destination:</label>
            <input
              type="checkbox"
              id="direction_same"
              name="direction_same"
              checked={formData.direction_same}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="direction_opp">Opposite direction from destination:</label>
            <input
              type="checkbox"
              id="direction_opp"
              name="direction_opp"
              checked={formData.direction_opp}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="form-section">
          <h3>Personal Information</h3>

          <div className="form-group">
            <label htmlFor="gender">Gender:</label>
            <select id="gender" name="gender" value={formData.gender} onChange={handleChange}>
              {renderEnumOptions(Gender)}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="age">Age:</label>
            <select id="age" name="age" value={formData.age} onChange={handleChange}>
              {renderEnumOptions(Age)}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="maritalStatus">Marital Status:</label>
            <select
              id="maritalStatus"
              name="maritalStatus"
              value={formData.maritalStatus}
              onChange={handleChange}
            >
              {renderEnumOptions(MaritalStatus)}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="has_children">Has Children:</label>
            <input
              type="checkbox"
              id="has_children"
              name="has_children"
              checked={formData.has_children}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="education">Education:</label>
            <select
              id="education"
              name="education"
              value={formData.education}
              onChange={handleChange}
            >
              {renderEnumOptions(Education)}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="occupation">Occupation:</label>
            <select
              id="occupation"
              name="occupation"
              value={formData.occupation}
              onChange={handleChange}
            >
              {renderEnumOptions(Occupation)}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="income">Income:</label>
            <select id="income" name="income" value={formData.income} onChange={handleChange}>
              {renderEnumOptions(Income)}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="car">Car Type:</label>
            <select id="car" name="car" value={formData.car} onChange={handleChange}>
              {renderEnumOptions(Car)}
            </select>
          </div>
        </div>

        <div className="form-section">
          <h3>Frequency Information</h3>

          <div className="form-group">
            <label htmlFor="Bar">Bar Visits:</label>
            <select id="Bar" name="Bar" value={formData.Bar} onChange={handleChange}>
              {renderEnumOptions(FrequencyCategory)}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="CoffeeHouse">Coffee House Visits:</label>
            <select
              id="CoffeeHouse"
              name="CoffeeHouse"
              value={formData.CoffeeHouse}
              onChange={handleChange}
            >
              {renderEnumOptions(FrequencyCategory)}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="CarryAway">Carry Away Food:</label>
            <select
              id="CarryAway"
              name="CarryAway"
              value={formData.CarryAway}
              onChange={handleChange}
            >
              {renderEnumOptions(FrequencyCategory)}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="RestaurantLessThan20">Restaurant &lt;$20 Visits:</label>
            <select
              id="RestaurantLessThan20"
              name="RestaurantLessThan20"
              value={formData.RestaurantLessThan20}
              onChange={handleChange}
            >
              {renderEnumOptions(FrequencyCategory)}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="Restaurant20To50">Restaurant $20-$50 Visits:</label>
            <select
              id="Restaurant20To50"
              name="Restaurant20To50"
              value={formData.Restaurant20To50}
              onChange={handleChange}
            >
              {renderEnumOptions(FrequencyCategory)}
            </select>
          </div>
        </div>

        <button type="submit" className="submit-button" disabled={loading}>
          {loading ? 'Predicting...' : 'Predict Coupon Acceptance'}
        </button>
      </form>

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      {result && (
        <div className="result-container">
          <h3>Prediction Result</h3>
          <p className="prediction">
            The user will {result.will_accept_coupon ? 'accept' : 'not accept'} the coupon.
          </p>
        </div>
      )}
    </div>
  );
};

export default CouponAssessmentForm;
